// src/pages/Budget_entering/api.js
// API-слой АРМ «Доходы и расходы». Реальные вызовы бэкенда.
//
// Контроллер: budget-report-arm-controller, база /api/arm/budget.
//   GET    /rows                   — перечень (серверная пагинация + фильтры)
//   POST   /rows                   — создание строки → { row, warnings[], period_integrity }
//   PUT    /rows/{id}              — правка строки  → { row, warnings[], period_integrity }
//   DELETE /rows/{id}              — удаление строки → period_integrity
//   POST   /import                 — загрузка файла (multipart)
//   GET    /sections               — справочник разделов (ответ НЕ типизирован в Swagger)
//   GET    /periods                — перечень периодов бюджета (ответ НЕ типизирован в Swagger)
//   GET    /periods/{id}/integrity — контроль баланса периода
//
// В контроллере нет GET /rows/{id} (загрузка одной строки для формы правки).
// Поэтому форма правки заполняется из строки, уже полученной списком —
// BudgetPage передаёт в RowFormModal весь объект строки, а не id.
//
// sections и periods Swagger отдаёт как generic-схему без полей — по словам
// бэкенда это штатная практика для всех их API, реальный ответ на проде
// нормальный, просто не описан в документации. Контракт по факту не
// подтверждён, поэтому здесь, как и в src/pages/Budget/api.js:
//   • pick() читает несколько вероятных вариантов имени поля;
//   • оставлены ВРЕМЕННЫЕ console.log [budgetEntering] — по ним доведём
//     маппинг и уберём defensive-фоллбэки.
//
// Числовые суммы (approved_budget, updated_budget, ...) POST/PUT ожидает
// строками (см. Swagger example: "approved_budget": "string") — вероятно,
// чтобы не терять точность при передаче. Отправляем как строку через amt().

import { getNetwork } from '../../utils/getNetwork';
import { API_ROOT } from '../../config';
import { SECTIONS, BUDGETS, TOLERANCE } from './mockData';

const ROWS_ENDPOINT = '/api/arm/budget/rows';
const SECTIONS_ENDPOINT = '/api/arm/budget/sections';
const PERIODS_ENDPOINT = '/api/arm/budget/periods';
const IMPORT_ENDPOINT = '/api/arm/budget/import';

export class ApiError extends Error {
  constructor(status, body) {
    super(`ApiError ${status}`);
    this.status = status;
    this.body = body || {};
  }
}

/* ------------------------------- утилиты ---------------------------------- */
const has = (v) => !(v === null || v === undefined || v === '');
const def = (v, d) => (v === null || v === undefined ? d : v);
const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
const pick = (o, key) => {
  if (o === null || o === undefined) return undefined;
  return o[key] !== undefined ? o[key] : o[toCamel(key)];
};

// Интерцептор getNetwork() гасит HTTP-ошибку в resolved response
// (см. utils/getNetwork.js) — статус проверяем руками, как в Budget/api.js.
async function call(fn, okStatuses = [200]) {
  try {
    const res = await fn();
    if (res && okStatuses.indexOf(res.status) !== -1) return res.data;
    if (res) throw new ApiError(res.status, res.data);
    throw new ApiError(0, null);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError((e.response && e.response.status) || 0, e.response && e.response.data);
  }
}

/* --------------------------------- meta ---------------------------------- */
function mapSection(s) {
  return {
    k: def(pick(s, 'k'), def(pick(s, 'code'), pick(s, 'section'))),
    part: def(pick(s, 'part'), pick(s, 'type')),
    ru: def(pick(s, 'ru'), pick(s, 'name_ru')),
    kk: def(pick(s, 'kk'), pick(s, 'name_kk')),
  };
}

// Виды бюджета (8/3/7) — под них в контроллере нет эндпоинта, список фиксирован.
export async function fetchBudgetMeta() {
  let sections = SECTIONS;
  try {
    const data = await call(() => getNetwork().get(API_ROOT + SECTIONS_ENDPOINT));
    // ⛔ ВРЕМЕННЫЙ ЛОГ — контракт не подтверждён, удалить после наладки.
    console.log('[budgetEntering] SECTIONS RAW:', JSON.stringify(data));
    const list = (Array.isArray(data) ? data : pick(data, 'sections') || pick(data, 'items') || [])
      .map(mapSection)
      .filter((s) => has(s.k));
    if (list.length) sections = list;
  } catch (e) {
    console.log('[budgetEntering] SECTIONS fetch failed, using local fallback:', e);
  }
  return { sections, budgets: BUDGETS, tolerance: TOLERANCE };
}

/* ------------------------ Перечень периодов бюджета ------------------------ */
function mapPeriod(p) {
  return {
    id: pick(p, 'id'),
    date: def(pick(p, 'date'), def(pick(p, 'upload_date'), pick(p, 'period'))),
    is_partial: def(pick(p, 'is_partial'), false),
  };
}

export async function fetchPeriods(budget) {
  const data = await call(() =>
    getNetwork().get(API_ROOT + PERIODS_ENDPOINT, { params: { budget } })
  );
  // ⛔ ВРЕМЕННЫЙ ЛОГ — контракт не подтверждён, удалить после наладки.
  console.log('[budgetEntering] PERIODS RAW:', JSON.stringify(data));
  const list = Array.isArray(data)
    ? data
    : pick(data, 'periods') || pick(data, 'items') || pick(data, 'content') || [];
  return list
    .map(mapPeriod)
    .filter((p) => has(p.date))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
}

/* -------------------------------- перечень строк ---------------------------- */
// Приводим ответ к плоскому виду, ожидаемому таблицей (sec/code/level/ub/adj/pp/ex/pct),
// но сохраняем и «сырые» поля — из них же строится форма правки (см. RowFormModal).
function toRow(item) {
  return {
    ...item,
    sec: item.section,
    code: item.code_full,
    level: item.depth,
    ub: item.updated_budget,
    adj: item.adjusted_budget,
    pp: item.payment_plan,
    ex: item.budget_execution_amount,
    pct: item.exec_to_plan_pct,
  };
}

export async function fetchRows(params) {
  const { budget, period, section = '', depth = '', q = '', page = 1, size = 20 } = params;
  // Параметры отправляются даже пустыми (не omitted) — бэкенд однажды падал
  // 500-й на GET /rows, когда section/depth/q отсутствовали в query вовсе;
  // похоже на NPE при отсутствующем опциональном параметре на его стороне.
  const data = await call(() =>
    getNetwork().get(API_ROOT + ROWS_ENDPOINT, {
      params: {
        budget,
        upload_date: period,
        section,
        depth,
        q,
        page,
        size,
      },
    })
  );
  return { rows: (data.items || []).map(toRow), total: def(data.total, 0) };
}

/* --------------------------- создание / правка ---------------------------- */
const amt = (v) => (has(v) ? String(v) : null);

function toApiPayload(payload) {
  const body = {
    budget: payload.budget,
    upload_date: payload.date,
    section: payload.sec,
    name: payload.name_missing ? '' : payload.name || '',
    name_missing: !!payload.name_missing,
    approved_budget: amt(payload.ab),
    updated_budget: amt(payload.ub),
    adjusted_budget: amt(payload.adj),
    payment_plan: amt(payload.pp),
    commitment_plan: amt(payload.cp),
    committed_amount: amt(payload.ca),
    unpaid_amount: amt(payload.ua),
    budget_execution_amount: amt(payload.ex),
  };
  for (let i = 1; i <= 6; i += 1) body[`code${i}`] = payload[`code${i}`] || '';
  return body;
}

function mapSaveResult(data) {
  return {
    row: data.row ? toRow(data.row) : null,
    warnings: data.warnings || [],
    period_integrity: data.period_integrity ? mapIntegrity(data.period_integrity) : null,
  };
}

export async function createRow(payload) {
  const data = await call(
    () => getNetwork().post(API_ROOT + ROWS_ENDPOINT, toApiPayload(payload)),
    [200, 201]
  );
  return mapSaveResult(data);
}

export async function updateRow(id, payload) {
  const data = await call(() =>
    getNetwork().put(`${API_ROOT}${ROWS_ENDPOINT}/${id}`, toApiPayload(payload))
  );
  return mapSaveResult(data);
}

export async function deleteRow(id) {
  const data = await call(() => getNetwork().delete(`${API_ROOT}${ROWS_ENDPOINT}/${id}`));
  return mapIntegrity(data);
}

/* ------------------------------- баланс ---------------------------------- */
function mapCheck(c) {
  return {
    id: c.check_code,
    key: c.check_code,
    level: c.level,
    formula: c.formula,
    residual: c.residual,
    ok: c.ok,
  };
}

function mapIntegrity(d) {
  return {
    budget: d.budget,
    period: d.upload_date,
    is_partial: d.is_partial,
    tolerance: def(d.tolerance, TOLERANCE),
    kpi: {
      resources: d.resources_total,
      uses: d.uses_total,
      residual: d.residual,
      deficit: d.deficit,
      is_surplus: d.deficit >= 0,
    },
    ok: d.ok,
    checks: (d.checks || []).map(mapCheck),
    missing_sections: d.missing_sections || null,
    carryover: d.carryover || null,
  };
}

// periodId — id периода (см. fetchPeriods), не дата: эндпоинт адресует период по id.
export async function fetchBalance(budget, periodId) {
  const data = await call(() =>
    getNetwork().get(`${API_ROOT}${PERIODS_ENDPOINT}/${periodId}/integrity`, {
      params: { budget },
    })
  );
  return mapIntegrity(data);
}

/* --------------------------- загрузка файла ------------------------------ */
export async function importFile({ budget, file }) {
  const form = new FormData();
  form.append('file', file);
  const data = await call(() =>
    getNetwork().post(`${API_ROOT}${IMPORT_ENDPOINT}`, form, { params: { budget } })
  );
  const integ = data.period_integrity ? mapIntegrity(data.period_integrity) : null;
  return {
    ok: true,
    kind: integ && integ.ok === false ? 'violation' : 'success',
    file_name: data.file_name,
    rows: def(data.rows_inserted, 0) + def(data.rows_replaced, 0),
    date: data.upload_date,
    kpi: integ ? integ.kpi : { resources: 0, uses: 0, residual: 0 },
    checks: integ ? integ.checks : [],
  };
}
